/**
 * L1J Web Client - GameState
 * 세계관, 캐릭터 목록, 위치 정보 등 게임 상태 관리
 */

import { EventEmitter, gameEvents, networkEvents } from './EventEmitter';
import { Entity, Player, Npc, Monster, Item, ChatMessage, WorldState, CharacterInfo, PlayerDetailStats } from './types';
import {
  AttackResultPayload,
  DoorStatusPayload,
  AttrDefPayload,
  BookmarkEntry,
  ShopSellListItem,
  ShopSellListPayload,
  ShopBuyListPayload,
  RetrieveListPayload,
} from '../types/network';
// 서버가 보내는 name_id($숫자) 패턴을 한글로 변환한다.
// nameResolver.ts는 다른 에이전트가 채우는 중이며, resolveNpcName은 매핑이 없거나
// 입력이 비어 있으면 원본 문자열을 그대로 돌려주므로 안전하다.
import { resolveItemLabel, resolveItemName, resolveNpcName, stripCountSuffix } from '../utils/nameResolver';

/**
 * `shop:buyList`의 항목 — 서버가 준 `{ objId, price }`(S_ShopBuyList는 이 둘뿐이다)에
 * 인벤토리에서 찾은 표시 정보를 붙인 것.
 *
 * 이름/아이콘/개수는 **못 찾으면 undefined로 남는다** (서버가 방금 사라진 아이템을
 * 실어 보내는 등의 경우). UI는 objId와 price만 있어도 그릴 수 있어야 한다.
 */
export interface ShopBuyEntry {
  /** 인벤토리 아이템의 objId (= `Item.id`). shop_result(resultType 1)의 `id`로 그대로 보낸다. */
  objId: number;
  /** NPC가 쳐주는 감정가 (소지 개수 전체의 값). */
  price: number;
  /** 표시명 — 개수 꼬리(`"물약 (7)"`)를 뗀 것. 인벤토리에 없으면 undefined. */
  name?: string;
  gfxId?: number;
  /** 소지 개수 (개수의 진실은 이 필드뿐이다 — 이름 꼬리가 아니다). */
  count?: number;
}

/** `shop:buyList` 발행 payload — 받은 것 + 인벤토리 조인 결과. */
export interface ShopBuyListState {
  /** 상점 NPC의 objId. */
  objId: number;
  /** 화폐 종류 (0x07 = 아데나). */
  currency: number;
  items: ShopBuyEntry[];
}

/**
 * `warehouse:list`의 항목 — 서버가 준 창고 아이템에 표시용 파생값을 붙인 것.
 *
 * 상점 목록과 달리 이름·아이콘·개수가 모두 패킷에 실려 오므로 인벤토리 조인이 없다.
 * 대신 상태 바이트가 감정 상태와 봉인 여부를 한 값에 섞어 보내 여기서 풀어 둔다.
 */
export interface WarehouseEntry {
  /** **창고** 아이템의 objId. 인출(shop_result resultType 3)의 `id`로 그대로 보낸다. */
  objId: number;
  gfxId: number;
  count: number;
  identified: boolean;
  /** 표시명 — 꼬리 개수를 떼고 `$숫자` 이름 ID·상태 라벨을 해석한 것. */
  name: string;
  /** 서버 원본 상태 바이트 (봉인 비트 128 포함). 그대로 두어 UI가 필요하면 쓰게 한다. */
  status: number;
  /** 감정/축복 상태 — 0=축복 1=일반 2=저주 3=미감정 (봉인 비트를 뗀 값). */
  blessStatus: number;
  /** 봉인 여부 (status의 128 비트). */
  sealed: boolean;
}

/** `warehouse:list` 발행 payload — 받은 것 + 이름 해석·상태 분해 결과. */
export interface WarehouseListState {
  /** 창고 NPC의 objId. `shop_result`의 `npcObjId`로 그대로 쓴다. */
  objId: number;
  /** 창고 종류 (3 = 개인 창고). */
  warehouseType: number;
  /** 인출에 드는 아데나 수수료. 모자라면 서버 메시지 189가 온다. */
  fee: number;
  items: WarehouseEntry[];
}

/** 창고 아이템 상태 바이트의 봉인 비트 (`status & 128`). */
const WAREHOUSE_SEALED_FLAG = 128;

/**
 * GameState 클래스
 * Singleton 패턴을 사용하여 게임 상태 관리
 */
/** 서버 ActionCodes.ACTION_Die — S_DoActionGFX의 사망 동작 ID. */
const ACTION_DIE = 8;

/**
 * S_Paralysis(opcode 165)가 보내는 원본 코드.
 * 서버는 상태이상 종류와 on/off를 한 바이트에 섞어 보낸다 (S_Paralysis.java 참조).
 */
/** 상태이상 시작 코드: 마비(2) / 마비2(4) / 수면(10) / 빙결(12) / 스턴(22) / 속박(24). */
const PARALYSIS_START_KINDS = new Set([2, 4, 10, 12, 22, 24]);
/** 상태이상 해제 코드: 위 코드들의 +1. */
const PARALYSIS_END_KINDS = new Set([3, 5, 11, 13, 23, 25]);
/** TYPE_TELEPORT_UNLOCK — 텔레포트 대기 상태 해제. */
const PARALYSIS_TELEPORT_UNLOCK = 7;

/**
 * 텔레포트 잠금 안전장치 (ms).
 * teleport_ack 이후 서버가 새 위치를 보내지 않는 이상 상황에서도 이동 입력이
 * 영구히 잠기지 않도록, 이 시간이 지나면 자동으로 잠금을 푼다.
 */
const TELEPORT_LOCK_TIMEOUT_MS = 8000;

/**
 * 인벤토리 원본 표시명을 상점 목록에 쓸 이름으로 바꾼다.
 *
 * 서버가 주는 그대로는 `"$326 ($11)"` 같은 모양이라 세 단계가 필요하다.
 *   1. 꼬리 개수 제거 — `"$26 (2)"`의 `(2)`를 떼야 `$26`이 매핑에 걸린다.
 *      개수의 진실은 `count` 필드뿐이다.
 *   2. 이름 ID 치환 — `$26` → `물약`
 *   3. 남은 `($숫자)` 상태 라벨 치환 — `($11)` → 축복/저주 등
 *
 * `Inventory`의 정보줄이 하는 것과 같은 처리다. 2단계까지만 하면 화면에
 * `광원 ($11)`처럼 라벨이 그대로 노출된다.
 */
function resolveShopItemName(rawName: string): string {
  const base = resolveItemName(stripCountSuffix(rawName ?? ''));
  if (!base.includes('($')) return base;
  return base.replace(/\(\$\d+\)/g, (m) => resolveItemLabel(m));
}

export class GameState {
  private static instance: GameState;
  private events: EventEmitter;

  // 플레이어 정보
  private playerObjId: number | null = null;
  /** S_OwnCharStatus로 받은 상세 스탯. 아직 못 받았으면 null. */
  private detailStats: PlayerDetailStats | null = null;
  /** 플레이어 외형 gfxId (클래스/성별). spawn에서 받아 씬이 스프라이트를 고른다. */
  private playerGfxId: number | null = null;
  private playerX: number = 0;
  private playerY: number = 0;
  private playerHeading: number = 0;
  /** 사망 여부. refreshDeathState()만이 갱신한다. */
  private dead: boolean = false;
  private hp: number = 0;
  private mp: number = 0;
  private maxHp: number = 0;
  private maxMp: number = 0;
  private level: number = 1;

  // 세계 정보
  private mapId: number = 0;
  /**
   * S_MapID를 한 번이라도 받았는지. **맵 0은 실재하는 맵이다**(말하는 섬·본토가
   * map_id 0이다). mapId의 초기값도 0이라 값만으로는 "아직 모른다"와 구별할 수
   * 없으므로, 아는지 여부는 이 플래그로만 판단한다.
   */
  private mapIdKnown: boolean = false;

  /** 텔레포트 진행 중 여부. true인 동안 이동 입력을 잠근다. */
  private teleporting: boolean = false;
  /** 텔레포트 잠금이 풀리지 않는 상황을 대비한 안전장치 타이머 ID. */
  private teleportTimer: number | null = null;

  /**
   * 엔티티별 장착 무기 코드 (S_CharVisualUpdate).
   * Entity 인터페이스(core/types.ts)에 필드를 넣지 않고 여기 별도로 보관한다.
   */
  private entityWeapons: Map<number, number> = new Map();

  /** 엔티티별 성향치 (S_Lawful). */
  private entityLawfuls: Map<number, number> = new Map();

  /**
   * 엔티티별 변신 gfxId (S_ChangeShape).
   * **변신 해제(polyId 0)는 값을 0으로 두지 않고 항목 자체를 지운다** —
   * "변신 중이 아니다"를 undefined 하나로 표현해 씬이 원래 스프라이트로 되돌리기 쉽게.
   */
  private entityPolyIds: Map<number, number> = new Map();

  /** 엔티티별 투명 코드 (S_Invis). 0이 아니면 보이지 않는 상태다. */
  private entityInvisTypes: Map<number, number> = new Map();

  /** 엔티티별 광원 반경 (S_Light, 타일). */
  private entityLights: Map<number, number> = new Map();

  /**
   * 엔티티별 외형 이상상태 색 (S_Poison). 1=초록(독), 2=회색(마비 등).
   * 정상(0)은 항목 자체를 지운다 — polyId와 같은 규칙이다.
   */
  private entityPoisonTypes: Map<number, number> = new Map();

  /** 엔티티별 칭호 (S_CharTitle). 빈 문자열이면 항목을 지운다. */
  private entityTitles: Map<number, string> = new Map();

  /** 마지막으로 받은 S_Weather 코드. 0 = 맑음. */
  private weatherCode: number = 0;

  /** 마지막으로 받은 S_Paralysis 원본 코드. 아직 못 받았으면 null. */
  private paralysisKind: number | null = null;
  /** 이동 불가 상태이상(마비/수면/빙결/스턴/속박) 여부. */
  private paralyzed: boolean = false;

  // 엔티티 컬렉션
  private players: Map<number, Player> = new Map();
  private npcs: Map<number, Npc> = new Map();
  private monsters: Map<number, Monster> = new Map();

  // 인벤토리
  private inventory: Item[] = [];

  // 채팅
  private chatMessages: ChatMessage[] = [];
  private maxChatMessages: number = 100;

  // 캐릭터 목록 (캐릭터 선택 화면용)
  private characterList: CharacterInfo[] = [];

  /**
   * 순간이동 북마크 목록 (S_BookmarkLoad).
   * 순간이동 주문서(use_type 6/29)가 요구하는 mapId + 북마크 objId의 유일한 출처다.
   */
  private bookmarks: BookmarkEntry[] = [];

  /**
   * 마지막으로 받은 상점 목록 (S_ShopSellList 254 / S_ShopBuyList 170).
   *
   * ⚠ **이름은 서버(NPC) 관점이다.** sellList는 NPC가 파는 물건 = 플레이어의
   * "구매" 목록이고, buyList는 NPC가 사주는 값 = 플레이어의 "판매" 목록이다.
   * 상점 창을 닫거나 맵이 바뀌면 비운다 (clearShopLists).
   */
  private shopSellList: ShopSellListPayload | null = null;
  private shopBuyList: ShopBuyListState | null = null;

  /**
   * 마지막으로 받은 창고 목록 (`npc_action("retrieve")`의 응답).
   *
   * 상점과 별개의 창이므로 목록도 따로 보관한다 — 창고 아이템의 objId는
   * 인벤토리 objId와 섞이면 안 된다 (보관은 인벤토리, 인출은 창고 objId).
   * 창을 닫거나 맵이 바뀌면 비운다 (clearWarehouseList).
   */
  private warehouseList: WarehouseListState | null = null;

  /**
   * 생성자
   */
  private constructor() {
    this.events = new EventEmitter();
    this.setupNetworkListeners();
  }

  /**
   * Singleton 인스턴스 반환
   */
  static getInstance(): GameState {
    if (!GameState.instance) {
      GameState.instance = new GameState();
    }
    return GameState.instance;
  }

  /**
   * 네트워크 이벤트 리스너 설정
   */
  private setupNetworkListeners(): void {
    // 게이트웨이는 char_list의 payload로 **배열 자체**를 보내며, 서버가 캐릭터를
    // 하나씩 따로 보내므로 이 메시지도 캐릭터당 한 번씩 도착한다
    // (ProtocolTranslator.translateCharList → Collections.singletonList).
    // 그래서 덮어쓰면 마지막 한 명만 남는다 — 누적해야 한다.
    // 과거 형태({ characters: [...] })도 방어적으로 수용한다.
    networkEvents.on('char_list', (data: CharacterInfo[] | { characters?: CharacterInfo[] }) => {
      const incoming = Array.isArray(data) ? data : (data?.characters ?? []);
      this.addCharacters(incoming);
    });

    networkEvents.on('char_spawn', (data: { objId: number; x: number; y: number; gfxId: number; heading: number; charName?: string }) => {
      if (data.charName) {
        this.addPlayer({
          objId: data.objId,
          x: data.x,
          y: data.y,
          gfxId: data.gfxId,
          heading: data.heading,
          account: '',
          charName: data.charName,
        });
      }
    });

    // 게이트웨이의 opcode 3 통합 스폰 (PC/NPC/Pet/Door/... 공통).
    // objId가 자신의 캐릭터와 일치하면 위치 갱신, 아니면 게이트웨이가 판별한
    // type("player"/"npc"/"monster")에 따라 알맞은 컬렉션에 추가한다.
    networkEvents.on('spawn', (data: { objId: number; x: number; y: number; gfxId: number; type?: string; name?: string; heading?: number }) => {
      if (this.playerObjId !== null && data.objId === this.playerObjId) {
        this.setPlayer(data.objId, data.x, data.y, data.gfxId);
        return;
      }
      const heading = data.heading ?? 0;
      if (data.type === 'player') {
        this.addPlayer({
          objId: data.objId,
          x: data.x,
          y: data.y,
          gfxId: data.gfxId,
          heading,
          account: '',
          charName: data.name || 'Player',
        });
      } else if (data.type === 'monster') {
        this.addMonster({
          objId: data.objId,
          x: data.x,
          y: data.y,
          gfxId: data.gfxId,
          heading,
          monsterId: 0,
          name: resolveNpcName(data.name || 'Monster'),
          level: 0,
        });
      } else {
        this.addNpc({
          objId: data.objId,
          x: data.x,
          y: data.y,
          gfxId: data.gfxId,
          heading,
          npcId: 0,
          name: resolveNpcName(data.name || 'NPC'),
        });
      }
    });

    // S_OwnCharStatus (opcode 145) — 자기 캐릭터 종합 상태.
    // "DB 접속중..." 화면을 벗어나는 핵심 트리거이자 스탯 갱신원.
    networkEvents.on('own_char_status', (data: PlayerDetailStats & { objId: number }) => {
      const isNewPlayerId = this.playerObjId !== data.objId;
      this.playerObjId = data.objId;

      // 상세 스탯(STR/AC/속성저항/무게 등)은 이 패킷에만 실려 온다.
      // 요약 스탯(updateStats)은 HP/MP/레벨만 남기므로 여기서 통째로 보관한다.
      //
      // SP/MR만은 예외다 — 이 스냅샷에 들어 있지 않고 S_SpMr로만 오므로,
      // 통째로 덮어쓰면 먼저 받아둔 값이 undefined로 지워진다. 이전 값을 잇는다.
      this.setDetailStats({
        ...data,
        sp: data.sp ?? this.detailStats?.sp ?? 0,
        mr: data.mr ?? this.detailStats?.mr ?? 0,
      });

      // own_char_status가 spawn보다 늦게 도착해 일반 엔티티로 잘못 등록된 경우 정리.
      // (spawn의 type 판별에 따라 players/monsters에 들어갔을 수도 있으므로 세 맵 모두 확인)
      if (isNewPlayerId) {
        const pending = this.npcs.get(data.objId) ?? this.players.get(data.objId) ?? this.monsters.get(data.objId);
        if (pending) {
          this.removeEntity(data.objId); // entity:remove 발생 → 씬에서 유령 스프라이트 정리
          this.setPlayer(data.objId, pending.x, pending.y, pending.gfxId);
        }
      }

      this.updateStats(data.currentHp, data.currentMp, data.maxHp, data.maxMp, data.level);
    });

    networkEvents.on('map_id', (data: { mapId: number; underwater?: boolean }) => {
      this.setMapId(data.mapId);
    });

    networkEvents.on('object_remove', (data: { objId: number }) => {
      this.removeEntity(data.objId);
    });

    networkEvents.on('move_object', (data: { objId: number; x: number; y: number; heading: number }) => {
      this.updateEntityPosition(data.objId, data.x, data.y, data.heading);
    });

    // 게이트웨이는 inventory의 payload로 아이템 **배열 자체**를 보낸다
    // (ProtocolTranslator.translateInvList → WebMessage.inventory(items)).
    // 과거 형태({ items: [...] })도 방어적으로 수용한다.
    networkEvents.on('inventory', (data: Item[] | { items?: Item[] }) => {
      const items = Array.isArray(data) ? data : (data?.items ?? []);
      this.setInventory(items.map((item) => this.normalizeItem(item)));
    });

    // S_AddItem (opcode 63) — 인벤토리 단건 추가. 게이트웨이 AddItemData:
    // { objId, gfxId, count, identified, name, ... } (objId가 클라이언트 Item.id에 대응)
    networkEvents.on('add_item', (data: {
      objId: number; gfxId: number; count: number; identified?: boolean; name?: string;
      useType?: number;
    }) => {
      if (typeof data?.objId !== 'number') return;
      this.upsertItem({
        id: data.objId,
        gfxId: data.gfxId ?? 0,
        count: data.count ?? 1,
        name: data.name ?? '',
        identified: data.identified ?? false,
        enchantLevel: 0,
        useType: data.useType,
      });
    });

    // S_DeleteInventoryItem (opcode 148) — 인벤토리 단건 제거. payload: { objId }
    networkEvents.on('delete_item', (data: { objId: number }) => {
      if (typeof data?.objId !== 'number') return;
      this.removeItem(data.objId);
    });

    // S_DoActionGFX (opcode 218) — 동작 알림.
    // **몹/캐릭터의 사망이 이 경로로 온다** (actionId 8 = ACTION_Die).
    // 서버는 사망 상태를 따로 보내지 않고 이 동작 패킷으로만 알린다.
    networkEvents.on('do_action', (data: { objId: number; actionId: number }) => {
      if (typeof data?.objId !== 'number') return;
      if (data.actionId !== ACTION_DIE) return;

      // 자기 캐릭터의 사망은 이 패킷이 **유일하게 확실한 근거**다.
      // (HP는 S_HpUpdate에서 1로 클램프돼 오므로 믿을 수 없다 — refreshDeathState 주석)
      if (data.objId === this.playerObjId) {
        this.setPlayerDead(true);
      } else {
        this.setDead(data.objId, true);
      }
    });

    // S_Resurrection (opcode 227) — 부활. 사망과 대칭으로 서버가 알려주는 신호다.
    // 게이트웨이가 이 opcode를 번역해야 도착한다 (ProtocolTranslator).
    networkEvents.on('resurrection', (data: { objId: number }) => {
      if (typeof data?.objId !== 'number') return;
      if (data.objId === this.playerObjId) {
        this.setPlayerDead(false);
      } else {
        this.setDead(data.objId, false);
      }
    });

    // S_ItemName (opcode 195) — 아이템 표시명 변경.
    // **장착/해제가 이 경로로 통지된다.** 서버는 장착 상태를 별도 필드로 보내지
    // 않고, 이름 끝에 붙는 접미사로만 알려준다.
    networkEvents.on('item_name', (data: { objId: number; name: string }) => {
      if (typeof data?.objId !== 'number') return;
      this.renameItem(data.objId, data.name ?? '');
    });

    // ⚠ 게이트웨이는 chat_message를 보내지 않는다 — 서버 채팅은 전부 'server_chat'으로 온다
    // (WebMessage.chatMessage() 빌더는 있으나 ProtocolTranslator가 호출하지 않는다).
    // 따라서 이 리스너는 실행되지 않는다. 'server_chat'은 GameScene이 받아
    // ChatBox 표시와 gameState.addChatMessage()를 함께 처리하므로, 여기서 또
    // 구독하면 채팅 상태에 같은 줄이 두 번 쌓인다.
    networkEvents.on('chat_message', (data: { type: string; sender: string; message: string }) => {
      this.addChatMessage({ ...data, timestamp: Date.now() });
    });

    // S_HpUpdate (opcode 42) — payload: { currentHp, maxHp }
    networkEvents.on('hp_update', (data: { currentHp: number; maxHp: number }) => {
      if (typeof data?.currentHp !== 'number') return;
      this.updateStats(data.currentHp, this.mp, data.maxHp ?? this.maxHp, this.maxMp);
    });

    // S_MpUpdate (opcode 73) — payload: { currentMp, maxMp } (hp_update와 대칭).
    // **구버전 게이트웨이는 bare int(currentMp)를 보낸다** (WebMessage.mpUpdate(int)).
    // 재빌드 전 구버전이 붙어 있을 수 있으므로 정수도 계속 받되, 그때는
    // maxMp를 알 수 없으므로 기존 값을 유지한다.
    networkEvents.on('mp_update', (data: number | { currentMp?: number; mp?: number; maxMp?: number }) => {
      const mp = typeof data === 'number' ? data : (data?.currentMp ?? data?.mp);
      if (typeof mp !== 'number') return;
      const maxMp = typeof data === 'number' ? this.maxMp : (data?.maxMp ?? this.maxMp);
      this.updateStats(this.hp, mp, this.maxHp, maxMp);
    });

    // S_Exp (opcode 121) — payload: { level, exp }
    networkEvents.on('exp_update', (data: { level: number; exp: number }) => {
      if (typeof data?.level !== 'number') return;
      this.updateStats(this.hp, this.mp, this.maxHp, this.maxMp, data.level);
    });

    // S_ItemAmount (opcode 127) — 인벤토리 단건의 개수/표시명 갱신.
    // **포션·화살처럼 쌓이는 아이템을 소비하면 이 패킷만 온다.** 개수가 0이 된
    // 뒤에야 delete_item이 오므로, 이걸 반영하지 않으면 "마셔도 개수가 그대로"가 된다.
    networkEvents.on('item_amount', (data: { objId: number; name?: string; count: number }) => {
      if (typeof data?.objId !== 'number') return;
      this.updateItemAmount(data.objId, data.count ?? 0, data.name);
    });

    // S_CharVisualUpdate (opcode 113) — 장착 무기 변경 등 외형 갱신.
    networkEvents.on('char_visual_update', (data: { objId: number; weapon: number }) => {
      if (typeof data?.objId !== 'number') return;
      this.setEntityWeapon(data.objId, data.weapon ?? 0);
    });

    // S_Lawful (opcode 140) — 성향치 갱신.
    networkEvents.on('lawful', (data: { objId: number; lawful: number }) => {
      if (typeof data?.objId !== 'number') return;
      this.setEntityLawful(data.objId, data.lawful ?? 0);
    });

    // S_ChangeShape (opcode 164) — 변신. polyId 0은 변신 해제(원래 모습)다.
    networkEvents.on('poly', (data: { objId: number; polyId: number; weaponTakeoff?: boolean }) => {
      if (typeof data?.objId !== 'number') return;
      this.setEntityPoly(data.objId, data.polyId ?? 0, data.weaponTakeoff ?? false);
    });

    // S_Invis (opcode 57) — 투명. invisType이 0이 아니면 보이지 않는 상태.
    networkEvents.on('invis', (data: { objId: number; invisType: number }) => {
      if (typeof data?.objId !== 'number') return;
      this.setEntityInvis(data.objId, data.invisType ?? 0);
    });

    // S_Light (opcode 53) — 광원 반경(타일).
    networkEvents.on('light', (data: { objId: number; lightSize: number }) => {
      if (typeof data?.objId !== 'number') return;
      this.setEntityLight(data.objId, data.lightSize ?? 0);
    });

    // S_ChangeHeading (opcode 199) — 제자리 방향 전환. 좌표는 오지 않는다.
    networkEvents.on('change_heading', (data: { objId: number; heading: number }) => {
      if (typeof data?.objId !== 'number') return;
      this.setEntityHeading(data.objId, data.heading ?? 0);
    });

    // S_OwnCharAttrDef (opcode 15) — AC/속성 방어력의 부분 갱신.
    // own_char_status가 싣고 오는 같은 필드들의 조각 패킷이라 상세 스탯에 덮어쓴다.
    networkEvents.on('attr_def', (data: Partial<AttrDefPayload>) => {
      if (typeof data?.ac !== 'number') return;
      this.applyAttrDef(data as AttrDefPayload);
    });

    // S_Paralysis (opcode 165) — 상태이상 on/off 및 텔레포트 대기 해제.
    networkEvents.on('paralysis', (data: { kind: number }) => {
      if (typeof data?.kind !== 'number') return;
      this.applyParalysis(data.kind);
    });

    // S_Poison (opcode 93) — 외형 이상상태 색 (0=정상 1=독 2=마비).
    networkEvents.on('poison', (data: { objId: number; type: number }) => {
      if (typeof data?.objId !== 'number') return;
      this.setEntityPoisonType(data.objId, data.type ?? 0);
    });

    // S_CharTitle (opcode 202) — 캐릭터 칭호.
    networkEvents.on('char_title', (data: { objId: number; title: string }) => {
      if (typeof data?.objId !== 'number') return;
      this.setEntityTitle(data.objId, data.title ?? '');
    });

    // S_SpMr (opcode 174) — SP/MR 부분 갱신. own_char_status와 같은 상세 스탯에 합친다.
    networkEvents.on('sp_mr', (data: { sp: number; mr: number }) => {
      if (typeof data?.sp !== 'number') return;
      this.applySpMr(data.sp, data.mr ?? 0);
    });

    // S_Strup (120) / S_Dexup (28) — 능력치 상승.
    networkEvents.on('stat_up', (data: { stat: string; value: number; weight: number }) => {
      if (typeof data?.value !== 'number') return;
      this.applyStatUp(data.stat, data.value, data.weight ?? -1);
      // 상승 자체를 알리는 연출은 씬이 맡는다 (스탯 반영과 별개).
      gameEvents.emit('player:statup', { ...data });
    });

    // S_AttackPacket / S_AttackMissPacket (opcode 142) — 전투 결과.
    // 보관할 상태가 없는 순수 연출이라 그대로 씬에 넘긴다.
    networkEvents.on('attack_result', (data: AttackResultPayload) => {
      if (typeof data?.targetObjId !== 'number') return;
      gameEvents.emit('combat:attack', {
        ...data,
        attackerIsPlayer: data.attackerObjId === this.playerObjId,
        targetIsPlayer: data.targetObjId === this.playerObjId,
      });
    });

    // S_Door (opcode 35) — 문 타일의 통행 가능 여부. 맵 충돌 갱신은 씬이 한다.
    networkEvents.on('door_status', (data: DoorStatusPayload) => {
      if (typeof data?.x !== 'number' || typeof data?.y !== 'number') return;
      gameEvents.emit('map:door', { ...data });
    });

    // S_CommonNews — 로그인 공지. 채팅창에 흘려보낸다.
    networkEvents.on('common_news', (data: { text: string }) => {
      if (!data?.text) return;
      gameEvents.emit('server:news', { text: data.text });
    });

    // S_Weather — 날씨 코드 (0 = 맑음).
    networkEvents.on('weather', (data: { code: number }) => {
      if (typeof data?.code !== 'number') return;
      this.weatherCode = data.code;
      gameEvents.emit('world:weather', { code: data.code });
    });

    // S_Teleport (opcode 4) — 텔레포트 시작. 대상은 언제나 자기 캐릭터다.
    // teleport_ack 송신과 화면 처리는 GameScene이 맡고, 여기서는 이동 입력을 잠근다.
    networkEvents.on('teleport', (data: { objId: number }) => {
      if (typeof data?.objId !== 'number') return;
      this.setTeleporting(true);
    });

    // S_BookmarkLoad — 순간이동 북마크 목록. payload는 배열 자체다.
    // 로그인/맵 이동 시점에 다시 올 수 있으므로 통째로 교체한다.
    // 과거 형태({ bookmarks: [...] })도 방어적으로 수용한다.
    networkEvents.on('bookmarks', (data: BookmarkEntry[] | { bookmarks?: BookmarkEntry[] }) => {
      const list = Array.isArray(data) ? data : (data?.bookmarks ?? []);
      this.setBookmarks(list);
    });

    // S_ShopSellList (opcode 254) — **NPC가 파는 물건 = 플레이어의 "구매" 목록**.
    // npc_action("buy")의 응답이다.
    networkEvents.on('shop_sell_list', (data: ShopSellListPayload) => {
      if (typeof data?.objId !== 'number') return;
      this.setShopSellList(data);
    });

    // S_ShopBuyList (opcode 170) — **NPC가 사주는 값 = 플레이어의 "판매" 목록**.
    // npc_action("sell")의 응답이며, 이름도 아이콘도 오지 않아 인벤토리와 조인한다.
    networkEvents.on('shop_buy_list', (data: ShopBuyListPayload) => {
      if (typeof data?.objId !== 'number') return;
      this.setShopBuyList(data);
    });

    // 창고 목록 — npc_action("retrieve")의 응답. 서버가 조건(드워프 + 레벨 5)에
    // 맞지 않으면 아무 것도 보내지 않으므로, 이 이벤트가 곧 "창고가 열렸다"는 신호다.
    networkEvents.on('retrieve_list', (data: RetrieveListPayload) => {
      if (typeof data?.objId !== 'number') return;
      this.setWarehouseList(data);
    });
  }

  /** 게이트웨이 payload에 없는 필드를 채워 클라이언트 Item 형태로 정규화한다. */
  private normalizeItem(item: Partial<Item> & { id?: number }): Item {
    return {
      id: item.id ?? 0,
      gfxId: item.gfxId ?? 0,
      count: item.count ?? 0,
      // 서버가 보내는 name_id($숫자) 패턴을 NPC_NAMES에서 조회해 한글로 변환한다.
      // 매핑이 없거나 입력이 비어 있으면 원본을 그대로 둔다.
      name: resolveNpcName(item.name ?? ''),
      identified: item.identified ?? false,
      enchantLevel: item.enchantLevel ?? 0,
      // use_item 송신 시 서버 C_UseItem의 인자 형태를 맞추는 데 쓴다.
      useType: item.useType,
    };
  }

  // ==================== 플레이어 getter ====================

  getPlayerObjId(): number | null { return this.playerObjId; }
  getPlayerX(): number { return this.playerX; }
  getPlayerY(): number { return this.playerY; }
  getPlayerHeading(): number { return this.playerHeading; }
  getHp(): number { return this.hp; }
  getMp(): number { return this.mp; }
  getMaxHp(): number { return this.maxHp; }
  getMaxMp(): number { return this.maxMp; }
  getLevel(): number { return this.level; }
  getMapId(): number { return this.mapId; }
  /** S_MapID를 받아 현재 맵을 알고 있는지. 맵 0이 유효하므로 값 비교로는 알 수 없다. */
  hasMapId(): boolean { return this.mapIdKnown; }

  getPlayerPosition(): { x: number; y: number; heading: number } {
    return { x: this.playerX, y: this.playerY, heading: this.playerHeading };
  }

  getPlayerStats(): { hp: number; mp: number; maxHp: number; maxMp: number; level: number } {
    return { hp: this.hp, mp: this.mp, maxHp: this.maxHp, maxMp: this.maxMp, level: this.level };
  }

  // ==================== 플레이어 setter ====================

  /**
   * 플레이어 설정 (스폰 시)
   */
  /**
   * 플레이어 식별/위치 설정.
   *
   * gfxId는 캐릭터 외형(클래스·성별)을 결정한다. 게이트웨이는 char_spawn을 보내지
   * 않고 opcode 3을 spawn으로 보내므로, 자기 캐릭터의 gfxId가 들어오는 경로는
   * 여기뿐이다. 이 값을 흘려보내지 않으면 씬이 외형을 기본값으로 그린다.
   */
  setPlayer(objId: number, x: number, y: number, gfxId?: number): void {
    this.playerObjId = objId;
    this.playerX = x;
    this.playerY = y;
    if (gfxId !== undefined) this.playerGfxId = gfxId;

    const payload = { objId, x, y, gfxId: this.playerGfxId };
    this.events.emit('player:set', payload);
    gameEvents.emit('player:set', payload);
  }

  /**
   * 상세 스탯을 갱신하고 변경을 알린다.
   * 서버는 스탯이 바뀔 때마다 S_OwnCharStatus를 다시 보내므로 통째로 교체한다.
   */
  private setDetailStats(stats: PlayerDetailStats): void {
    this.detailStats = { ...stats };
    this.events.emit('player:detail', this.detailStats);
    gameEvents.emit('player:detail', this.detailStats);
  }

  /** 상세 스탯. 아직 S_OwnCharStatus를 못 받았으면 null. */
  getDetailStats(): PlayerDetailStats | null {
    return this.detailStats ? { ...this.detailStats } : null;
  }

  /**
   * S_OwnCharAttrDef(opcode 15)로 온 AC/속성 방어력을 상세 스탯에 덮어쓴다.
   *
   * 이 패킷은 S_OwnCharStatus가 싣고 오는 스냅샷의 **부분 갱신판**이다 (같은 다섯 필드를
   * 장비 교체 시점에만 다시 보낸다). 그래서 새 이벤트를 만들지 않고 setDetailStats를 거쳐
   * 같은 `player:detail`을 재발행한다 — 상세 창이 두 경로를 구독할 이유가 없다.
   *
   * **ac는 음수가 정상이다** (실측 -13). 부호를 잘라내지 말 것.
   *
   * S_OwnCharStatus를 아직 못 받았으면 덮어쓸 스냅샷이 없으므로 버린다. 여기서 조각을
   * 들고 있다가 나중에 합치면, 뒤이어 도착한 더 새로운 스냅샷을 낡은 값으로 오염시킨다.
   */
  applyAttrDef(attr: AttrDefPayload): void {
    if (!this.detailStats) return;

    const next: PlayerDetailStats = {
      ...this.detailStats,
      ac: attr.ac,
      fire: attr.fire ?? this.detailStats.fire,
      water: attr.water ?? this.detailStats.water,
      wind: attr.wind ?? this.detailStats.wind,
      earth: attr.earth ?? this.detailStats.earth,
    };

    // 다섯 필드가 모두 그대로면 화면을 다시 그릴 이유가 없다.
    const prev = this.detailStats;
    if (prev.ac === next.ac && prev.fire === next.fire && prev.water === next.water
      && prev.wind === next.wind && prev.earth === next.earth) {
      return;
    }

    this.setDetailStats(next);
  }

  /** 플레이어 외형 gfxId (클래스/성별). 미수신이면 null. */
  getPlayerGfxId(): number | null {
    return this.playerGfxId;
  }

  /**
   * 플레이어 위치 업데이트
   */
  updatePlayerPosition(x: number, y: number, heading: number): void {
    this.playerX = x;
    this.playerY = y;
    this.playerHeading = heading;
    this.events.emit('player:position', { x, y, heading });
    gameEvents.emit('player:position', { x, y, heading });
  }

  /**
   * 플레이어 스탯 업데이트
   */
  updateStats(hp: number, mp: number, maxHp: number, maxMp: number, level?: number): void {
    hp = this.normalizeHp(hp);

    let changed = false;
    if (this.hp !== hp) { this.hp = hp; changed = true; }
    if (this.mp !== mp) { this.mp = mp; changed = true; }
    if (this.maxHp !== maxHp) { this.maxHp = maxHp; changed = true; }
    if (this.maxMp !== maxMp) { this.maxMp = maxMp; changed = true; }
    if (level !== undefined && this.level !== level) { this.level = level; changed = true; }

    if (changed) {
      this.events.emit('player:stats', { hp: this.hp, mp: this.mp, maxHp: this.maxHp, maxMp: this.maxMp, level: this.level });
      gameEvents.emit('player:stats', { hp: this.hp, mp: this.mp, maxHp: this.maxHp, maxMp: this.maxMp, level: this.level });
    }

    this.refreshDeathState();
  }

  /**
   * HP만 업데이트
   */
  updateHp(hp: number): void {
    this.hp = this.normalizeHp(hp);
    this.events.emit('player:stats', { hp: this.hp, mp: this.mp, maxHp: this.maxHp, maxMp: this.maxMp });
    gameEvents.emit('player:hp', { hp: this.hp, maxHp: this.maxHp });

    this.refreshDeathState();
  }

  /**
   * 죽어 있는 동안 도착한 HP 1을 0으로 되돌린다.
   *
   * 서버 `S_HpUpdate`는 HP를 1 이상으로 클램프해 보내므로(`S_HpUpdate.hpRange`)
   * 죽은 캐릭터의 HP도 1로 온다. 그대로 두면 화면에 "1 / 89"처럼 살아 있는 것처럼
   * 표시된다. 사망이 확정된 상태에서는 0이 진실이다.
   *
   * 살아 있을 때의 HP 1(빈사)은 건드리지 않는다.
   */
  private normalizeHp(hp: number): number {
    return this.dead && hp === 1 ? 0 : hp;
  }

  /**
   * HP 변화로부터 사망/부활 상태를 갱신하고, 전이가 있을 때만 이벤트를 발행한다.
   *
   * **maxHp가 0인 동안은 판정하지 않는다.** hp/maxHp의 초기값이 모두 0이라
   * 접속 직후 서버 스탯(S_OwnCharStatus)을 받기 전 구간이 hp <= 0 조건에 걸린다.
   * 이때 사망으로 보면 살아 있는데도 처음부터 이동이 막힌다.
   */
  private refreshDeathState(): void {
    if (this.maxHp <= 0) return;

    // **HP 1은 사망/부활 어느 쪽의 근거도 되지 못한다.**
    // 서버 `S_HpUpdate`는 `hpRange = IntRange(1, 32767)`로 값을 클램프해 보내므로
    // (`S_HpUpdate.java`), 죽어서 HP가 0이어도 클라이언트에는 1이 도착한다.
    // 진짜 1(빈사)과 구분할 방법이 없다. 이 값으로 상태를 바꾸면 사망 직후
    // 도착하는 hp_update(1)이 곧바로 "부활"로 읽혀 사망 연출이 지워진다.
    //
    // 클램프 없이 진짜 HP를 싣는 것은 `S_OwnCharStatus`(0을 그대로 보낸다)뿐이고,
    // 사망의 확실한 신호는 `S_DoActionGFX(ACTION_Die)`다 → setPlayerDead().
    if (this.hp === 1) return;

    this.setPlayerDead(this.hp <= 0);
  }

  /**
   * 플레이어의 사망/부활을 확정한다. 전이가 있을 때만 이벤트를 발행한다.
   *
   * HP 추정(refreshDeathState)과 서버 신호(do_action / resurrection)가 모두
   * 이 한 곳을 거치게 해서, 어느 경로로 들어와도 상태와 이벤트가 어긋나지 않게 한다.
   */
  private setPlayerDead(dead: boolean): void {
    if (dead === this.dead) return;

    this.dead = dead;

    // 사망이 확정되면 이미 받아 둔 클램프 HP(1)를 0으로 정정한다.
    // do_action(ACTION_Die)만 오고 S_OwnCharStatus가 뒤따르지 않는 경우에도
    // 화면이 "1 / 89"로 남지 않게 한다 (normalizeHp 주석 참조).
    if (dead && this.hp === 1) {
      this.hp = 0;
      const stats = { hp: this.hp, mp: this.mp, maxHp: this.maxHp, maxMp: this.maxMp, level: this.level };
      this.events.emit('player:stats', stats);
      gameEvents.emit('player:stats', stats);
    }

    const payload = { hp: this.hp, maxHp: this.maxHp };
    const event = dead ? 'player:death' : 'player:revive';
    this.events.emit(event, payload);
    gameEvents.emit(event, payload);
  }

  /**
   * 플레이어 사망 여부.
   *
   * 판단 근거는 두 가지다 — `S_DoActionGFX(ACTION_Die)`(확실한 신호)와
   * `S_OwnCharStatus`의 HP 0(클램프되지 않는 유일한 HP 경로). `S_HpUpdate`의
   * HP는 서버가 1 이상으로 클램프하므로 근거로 쓰지 않는다 (refreshDeathState 주석).
   */
  isDead(): boolean {
    return this.dead;
  }

  /**
   * 임의의 엔티티(자기/다른 PC/NPC/몹)에 대해 사망 상태를 강제로 설정한다.
   *
   * 서버의 S_DoActionGFX(사망 액션)를 받은 do_action 핸들러가 호출한다.
   * 호출하면 동일 objId로 `entity:death` 또는 `entity:revive` 이벤트가 발행되고,
   * GameScene이 그 신호로 사망 애니메이션을 시작한다.
   *
   * @param objId  대상 엔티티 objId
   * @param dead   true=사망, false=부활
   */
  setDead(objId: number, dead: boolean): void {
    if (dead) {
      gameEvents.emit('entity:death', { objId });
    } else {
      gameEvents.emit('entity:revive', { objId });
    }
  }

  /**
   * MP만 업데이트
   */
  updateMp(mp: number): void {
    this.mp = mp;
    this.events.emit('player:stats', { hp: this.hp, mp: this.mp, maxHp: this.maxHp, maxMp: this.maxMp });
    gameEvents.emit('player:mp', { mp: this.mp, maxMp: this.maxMp });
  }

  // ==================== 텔레포트 ====================

  /**
   * 텔레포트 진행 상태를 설정한다.
   *
   * true인 동안 GameScene은 이동 입력을 무시해야 한다 (서버가 좌표를 옮기는
   * 중이라 이때 보낸 이동 패킷은 엉뚱한 출발지로 해석된다).
   * 새 위치가 도착하면(spawn/own_char_status) GameScene이 false로 되돌린다.
   * 되돌리는 신호가 끝내 오지 않는 경우를 대비해 안전장치 타이머를 함께 건다.
   */
  setTeleporting(teleporting: boolean): void {
    this.clearTeleportTimer();

    if (teleporting) {
      this.teleportTimer = window.setTimeout(() => {
        this.teleportTimer = null;
        console.warn('GameState: teleport lock timed out, unlocking movement');
        this.setTeleporting(false);
      }, TELEPORT_LOCK_TIMEOUT_MS);
    }

    if (this.teleporting === teleporting) return;

    this.teleporting = teleporting;
    this.events.emit('player:teleport', { teleporting });
    gameEvents.emit('player:teleport', { teleporting });
  }

  /** 텔레포트 진행 중이면 true (이동 입력을 잠가야 하는 구간). */
  isTeleporting(): boolean {
    return this.teleporting;
  }

  private clearTeleportTimer(): void {
    if (this.teleportTimer !== null) {
      window.clearTimeout(this.teleportTimer);
      this.teleportTimer = null;
    }
  }

  // ==================== 상태이상 (S_Paralysis) ====================

  /**
   * S_Paralysis 원본 코드를 해석해 상태를 갱신한다.
   *
   * 코드 7(TYPE_TELEPORT_UNLOCK)은 상태이상이 아니라 텔레포트 대기 해제이므로
   * 이동 잠금만 푼다.
   */
  applyParalysis(kind: number): void {
    this.paralysisKind = kind;

    if (kind === PARALYSIS_TELEPORT_UNLOCK) {
      this.setTeleporting(false);
    } else if (PARALYSIS_START_KINDS.has(kind)) {
      this.paralyzed = true;
    } else if (PARALYSIS_END_KINDS.has(kind)) {
      this.paralyzed = false;
    }

    const payload = {
      kind,
      paralyzed: this.paralyzed,
      teleportUnlock: kind === PARALYSIS_TELEPORT_UNLOCK,
    };
    this.events.emit('player:paralysis', payload);
    gameEvents.emit('player:paralysis', payload);
  }

  /** 이동 불가 상태이상(마비/수면/빙결/스턴/속박) 여부. */
  isParalyzed(): boolean {
    return this.paralyzed;
  }

  /** 마지막으로 받은 S_Paralysis 원본 코드. 아직 못 받았으면 null. */
  getParalysisKind(): number | null {
    return this.paralysisKind;
  }

  /**
   * 지금 이동 입력을 받아도 되는지.
   * 사망·상태이상·텔레포트 중이면 false.
   */
  canMove(): boolean {
    return !this.dead && !this.paralyzed && !this.teleporting;
  }

  // ==================== 맵 ====================

  /**
   * 현재 맵 설정
   */
  setMapId(mapId: number): void {
    // 첫 통지는 값이 같아도(맵 0 진입) 반드시 흘려보낸다 — 안 그러면 맵 0에서만
    // map:change가 한 번도 발생하지 않는다.
    if (!this.mapIdKnown || this.mapId !== mapId) {
      this.mapIdKnown = true;
      this.mapId = mapId;
      // 엔티티 초기화
      this.players.clear();
      this.npcs.clear();
      this.monsters.clear();
      // 엔티티 부가 상태도 함께 정리한다 (자기 캐릭터 값은 유지).
      this.clearEntityAttributes(this.playerObjId);
      // 맵이 바뀌면 상점/창고 NPC와 멀어진 것이므로 목록도 함께 버린다
      // (서버도 3타일을 넘으면 C_Result를 무시한다).
      this.clearShopLists();
      this.clearWarehouseList();
      this.events.emit('map:change', { mapId });
      gameEvents.emit('map:change', { mapId });
    }
  }

  // ==================== 엔티티 관리 ====================

  /**
   * 플레이어 추가
   */
  addPlayer(player: Player): void {
    this.players.set(player.objId, player);
    this.events.emit('entity:spawn', { type: 'player', entity: player });
    gameEvents.emit('entity:spawn', { type: 'player', entity: player });
  }

  /**
   * NPC 추가
   */
  addNpc(npc: Npc): void {
    this.npcs.set(npc.objId, npc);
    this.events.emit('entity:spawn', { type: 'npc', entity: npc });
    gameEvents.emit('entity:spawn', { type: 'npc', entity: npc });
  }

  /**
   * 몬스터 추가
   */
  addMonster(monster: Monster): void {
    this.monsters.set(monster.objId, monster);
    this.events.emit('entity:spawn', { type: 'monster', entity: monster });
    gameEvents.emit('entity:spawn', { type: 'monster', entity: monster });
  }

  /**
   * 엔티티 제거
   */
  removeEntity(objId: number): void {
    let removed = false;
    if (this.players.delete(objId)) removed = true;
    if (this.npcs.delete(objId)) removed = true;
    if (this.monsters.delete(objId)) removed = true;

    // 자기 캐릭터의 부가 상태(무기/성향/변신/투명/광원)는 화면에서 사라져도
    // 계속 쓰이므로 남긴다.
    if (objId !== this.playerObjId) {
      this.entityWeapons.delete(objId);
      this.entityLawfuls.delete(objId);
      this.entityPolyIds.delete(objId);
      this.entityInvisTypes.delete(objId);
      this.entityLights.delete(objId);
      this.entityPoisonTypes.delete(objId);
      this.entityTitles.delete(objId);
    }

    if (removed) {
      this.events.emit('entity:remove', { objId });
      gameEvents.emit('entity:remove', { objId });
    }
  }

  /**
   * 엔티티 위치 업데이트
   */
  updateEntityPosition(objId: number, x: number, y: number, heading: number): void {
    let entity: Entity | undefined = this.players.get(objId);
    if (entity) {
      entity.x = x; entity.y = y; entity.heading = heading;
      this.events.emit('entity:move', { objId, x, y, heading, type: 'player' });
      gameEvents.emit('entity:move', { objId, x, y, heading, type: 'player' });
      return;
    }

    entity = this.npcs.get(objId);
    if (entity) {
      entity.x = x; entity.y = y; entity.heading = heading;
      this.events.emit('entity:move', { objId, x, y, heading, type: 'npc' });
      gameEvents.emit('entity:move', { objId, x, y, heading, type: 'npc' });
      return;
    }

    entity = this.monsters.get(objId);
    if (entity) {
      entity.x = x; entity.y = y; entity.heading = heading;
      this.events.emit('entity:move', { objId, x, y, heading, type: 'monster' });
      gameEvents.emit('entity:move', { objId, x, y, heading, type: 'monster' });
    }
  }

  /**
   * 엔티티의 방향만 갱신한다 (S_ChangeHeading). **좌표는 건드리지 않는다** —
   * 서버가 제자리 회전에만 보내는 패킷이라 위치가 실려 오지 않는다.
   *
   * 보관 자리는 새로 만들지 않고 updateEntityPosition()이 쓰는 것과 같은
   * `entity.heading` / `playerHeading`을 그대로 갱신한다. 자기 캐릭터는 엔티티
   * 맵에 없으므로(setPlayer가 별도 필드로 관리) playerHeading 쪽으로 간다.
   */
  setEntityHeading(objId: number, heading: number): void {
    const isPlayer = objId === this.playerObjId;

    if (isPlayer) {
      if (this.playerHeading === heading) return;
      this.playerHeading = heading;
    } else {
      const entity: Entity | undefined =
        this.players.get(objId) ?? this.npcs.get(objId) ?? this.monsters.get(objId);
      // 아직 스폰을 못 받은 오브젝트면 보관할 자리가 없다 — 스폰 패킷이 heading을
      // 함께 실어 오므로 흘려보내도 어긋나지 않는다.
      if (!entity) return;
      if (entity.heading === heading) return;
      entity.heading = heading;
    }

    const payload = { objId, heading, isPlayer };
    this.events.emit('entity:heading', payload);
    gameEvents.emit('entity:heading', payload);
  }

  /**
   * 모든 플레이어 반환
   */
  getPlayers(): Map<number, Player> {
    return new Map(this.players);
  }

  /**
   * 모든 NPC 반환
   */
  getNpcs(): Map<number, Npc> {
    return new Map(this.npcs);
  }

  /**
   * 모든 몬스터 반환
   */
  getMonsters(): Map<number, Monster> {
    return new Map(this.monsters);
  }

  /**
   * 특정 플레이어 반환
   */
  getPlayer(objId: number): Player | undefined {
    return this.players.get(objId);
  }

  /**
   * 특정 NPC 반환
   */
  getNpc(objId: number): Npc | undefined {
    return this.npcs.get(objId);
  }

  /**
   * 특정 몬스터 반환
   */
  getMonster(objId: number): Monster | undefined {
    return this.monsters.get(objId);
  }

  // ============ 엔티티 부가 상태 (무기 / 성향 / 변신 / 투명 / 광원) ============

  /**
   * 엔티티의 장착 무기 코드를 보관하고 변경을 알린다 (S_CharVisualUpdate).
   *
   * 서버 `L1PcInstance.getCurrentWeapon()` 값으로, 0=맨손이며 무기 종류마다 코드가
   * 다르다. GameScene은 이 값으로 캐릭터 스프라이트의 무기 변형을 고른다.
   * Entity 인터페이스(core/types.ts)는 다른 담당자의 파일이라 손대지 않고
   * 별도 Map으로 들고 있는다.
   */
  setEntityWeapon(objId: number, weapon: number): void {
    if (this.entityWeapons.get(objId) === weapon) return;

    this.entityWeapons.set(objId, weapon);

    const payload = { objId, weapon, isPlayer: objId === this.playerObjId };
    this.events.emit('entity:visual', payload);
    gameEvents.emit('entity:visual', payload);
  }

  /** 엔티티의 장착 무기 코드. 아직 통지받지 못했으면 undefined. */
  getEntityWeapon(objId: number): number | undefined {
    return this.entityWeapons.get(objId);
  }

  /** 자기 캐릭터의 장착 무기 코드 (미수신이면 0 = 맨손). */
  getPlayerWeapon(): number {
    if (this.playerObjId === null) return 0;
    return this.entityWeapons.get(this.playerObjId) ?? 0;
  }

  /**
   * 엔티티의 성향치를 보관하고 변경을 알린다 (S_Lawful).
   *
   * 자기 캐릭터라면 상세 스탯(S_OwnCharStatus)의 lawful도 함께 맞춰 준다.
   * 성향은 이 패킷으로 수시로 바뀌지만 S_OwnCharStatus는 자주 오지 않기 때문이다.
   */
  setEntityLawful(objId: number, lawful: number): void {
    if (this.entityLawfuls.get(objId) === lawful) return;

    this.entityLawfuls.set(objId, lawful);

    const payload = { objId, lawful, isPlayer: objId === this.playerObjId };
    this.events.emit('entity:lawful', payload);
    gameEvents.emit('entity:lawful', payload);

    if (objId === this.playerObjId && this.detailStats) {
      this.setDetailStats({ ...this.detailStats, lawful });
    }
  }

  /** 엔티티의 성향치. 아직 통지받지 못했으면 undefined. */
  getEntityLawful(objId: number): number | undefined {
    return this.entityLawfuls.get(objId);
  }

  /** 자기 캐릭터의 성향치 (S_Lawful 미수신이면 상세 스탯 값, 그것도 없으면 0). */
  getPlayerLawful(): number {
    if (this.playerObjId !== null) {
      const lawful = this.entityLawfuls.get(this.playerObjId);
      if (lawful !== undefined) return lawful;
    }
    return this.detailStats?.lawful ?? 0;
  }

  /**
   * 엔티티의 변신 상태를 보관하고 변경을 알린다 (S_ChangeShape).
   *
   * polyId는 변신할 **몹의 gfxId**이며, **0이면 변신 해제**(원래 모습)다.
   * 해제일 때는 0을 넣어 두지 않고 항목을 지워 getEntityPoly()가 undefined를 돌려주게 한다.
   * 그려 주는 쪽(GameScene)은 이 이벤트를 받아 스프라이트를 갈아 끼운다.
   *
   * @param weaponTakeoff 변신과 함께 무기가 벗겨지는지 (서버가 0/29로 보내는 플래그).
   *                      전이 순간에만 의미가 있어 보관하지 않고 이벤트로만 흘린다.
   */
  setEntityPoly(objId: number, polyId: number, weaponTakeoff: boolean = false): void {
    const before = this.entityPolyIds.get(objId);
    if ((before ?? 0) === polyId) return;

    if (polyId === 0) {
      this.entityPolyIds.delete(objId);
    } else {
      this.entityPolyIds.set(objId, polyId);
    }

    const payload = {
      objId,
      polyId,
      weaponTakeoff,
      isPlayer: objId === this.playerObjId,
    };
    this.events.emit('entity:poly', payload);
    gameEvents.emit('entity:poly', payload);
  }

  /** 엔티티의 변신 gfxId. 변신 중이 아니면 undefined. */
  getEntityPoly(objId: number): number | undefined {
    return this.entityPolyIds.get(objId);
  }

  /** 자기 캐릭터의 변신 gfxId (변신 중이 아니면 undefined). */
  getPlayerPoly(): number | undefined {
    if (this.playerObjId === null) return undefined;
    return this.entityPolyIds.get(this.playerObjId);
  }

  /**
   * 엔티티의 투명 상태를 보관하고 변경을 알린다 (S_Invis).
   *
   * invisType은 서버가 보내는 원본 코드이며 **0이 아니면 보이지 않는 상태**다.
   * 0(해제)이면 항목을 지운다.
   */
  setEntityInvis(objId: number, invisType: number): void {
    const before = this.entityInvisTypes.get(objId);
    if ((before ?? 0) === invisType) return;

    if (invisType === 0) {
      this.entityInvisTypes.delete(objId);
    } else {
      this.entityInvisTypes.set(objId, invisType);
    }

    const payload = {
      objId,
      invisType,
      invisible: invisType !== 0,
      isPlayer: objId === this.playerObjId,
    };
    this.events.emit('entity:invis', payload);
    gameEvents.emit('entity:invis', payload);
  }

  /** 엔티티의 투명 코드 (보이는 상태면 0). */
  getEntityInvisType(objId: number): number {
    return this.entityInvisTypes.get(objId) ?? 0;
  }

  /** 엔티티가 투명 상태인지 (invisType이 0이 아님). */
  isEntityInvisible(objId: number): boolean {
    return this.getEntityInvisType(objId) !== 0;
  }

  /**
   * 엔티티의 외형 이상상태 색을 보관하고 변경을 알린다 (S_Poison).
   *
   * 서버는 색만 알려준다 — 독인지 저주인지는 구분해 보내지 않으므로 이름도
   * 색 그대로 둔다. 정상(0)으로 돌아오면 항목을 지운다.
   */
  setEntityPoisonType(objId: number, poisonType: number): void {
    const before = this.entityPoisonTypes.get(objId) ?? 0;
    if (before === poisonType) return;

    if (poisonType === 0) {
      this.entityPoisonTypes.delete(objId);
    } else {
      this.entityPoisonTypes.set(objId, poisonType);
    }

    const payload = {
      objId,
      poisonType,
      isPlayer: objId === this.playerObjId,
    };
    this.events.emit('entity:poison', payload);
    gameEvents.emit('entity:poison', payload);
  }

  /** 엔티티의 이상상태 색 (정상이면 0). */
  getEntityPoisonType(objId: number): number {
    return this.entityPoisonTypes.get(objId) ?? 0;
  }

  /**
   * 엔티티의 칭호를 보관하고 변경을 알린다 (S_CharTitle).
   * 빈 문자열은 "칭호 없음"이므로 항목을 지운다.
   */
  setEntityTitle(objId: number, title: string): void {
    const clean = (title ?? '').trim();
    const before = this.entityTitles.get(objId) ?? '';
    if (before === clean) return;

    if (clean) {
      this.entityTitles.set(objId, clean);
    } else {
      this.entityTitles.delete(objId);
    }

    const payload = { objId, title: clean, isPlayer: objId === this.playerObjId };
    this.events.emit('entity:title', payload);
    gameEvents.emit('entity:title', payload);
  }

  /** 엔티티의 칭호. 없으면 빈 문자열. */
  getEntityTitle(objId: number): string {
    return this.entityTitles.get(objId) ?? '';
  }

  /** 마지막으로 받은 날씨 코드. 0 = 맑음. */
  getWeatherCode(): number {
    return this.weatherCode;
  }

  /**
   * S_SpMr(opcode 174)로 온 SP/MR을 상세 스탯에 덮어쓴다.
   *
   * attr_def와 같은 부분 갱신 패킷이라 새 이벤트를 만들지 않고 `player:detail`을
   * 재발행한다. 스냅샷(S_OwnCharStatus)을 아직 못 받았으면 합칠 대상이 없으므로 버린다.
   */
  applySpMr(sp: number, mr: number): void {
    if (!this.detailStats) return;
    if (this.detailStats.sp === sp && this.detailStats.mr === mr) return;
    this.setDetailStats({ ...this.detailStats, sp, mr });
  }

  /**
   * S_Strup(120) / S_Dexup(28)로 온 능력치 상승을 상세 스탯에 반영한다.
   *
   * `value`는 상승 후의 값이다. `weight`는 S_Strup에만 있어 S_Dexup에서는 -1로
   * 오므로 그때는 기존 값을 유지한다.
   */
  applyStatUp(stat: string, value: number, weight: number): void {
    if (!this.detailStats) return;

    const next = { ...this.detailStats };
    if (stat === 'str') next.str = value;
    else if (stat === 'dex') next.dex = value;
    else return; // 서버에 STR/DEX 외의 상승 패킷은 없다

    if (weight >= 0) next.weight = weight;

    if (next.str === this.detailStats.str && next.dex === this.detailStats.dex
      && next.weight === this.detailStats.weight) {
      return;
    }
    this.setDetailStats(next);
  }

  /**
   * 엔티티의 광원 반경을 보관하고 변경을 알린다 (S_Light).
   * lightSize는 시야 반경(타일)이며, 램프/횃불 장착이나 라이트 마법으로 바뀐다.
   */
  setEntityLight(objId: number, lightSize: number): void {
    if (this.entityLights.get(objId) === lightSize) return;

    this.entityLights.set(objId, lightSize);

    const payload = { objId, lightSize, isPlayer: objId === this.playerObjId };
    this.events.emit('entity:light', payload);
    gameEvents.emit('entity:light', payload);
  }

  /** 엔티티의 광원 반경(타일). 아직 통지받지 못했으면 undefined. */
  getEntityLight(objId: number): number | undefined {
    return this.entityLights.get(objId);
  }

  /** 자기 캐릭터의 광원 반경 (미수신이면 0). */
  getPlayerLight(): number {
    if (this.playerObjId === null) return 0;
    return this.entityLights.get(this.playerObjId) ?? 0;
  }

  /**
   * 엔티티 부가 상태를 비운다.
   * @param keepObjId 유지할 objId (보통 자기 캐릭터). null이면 전부 비운다.
   */
  private clearEntityAttributes(keepObjId: number | null): void {
    const keptWeapon = keepObjId !== null ? this.entityWeapons.get(keepObjId) : undefined;
    const keptLawful = keepObjId !== null ? this.entityLawfuls.get(keepObjId) : undefined;
    const keptPoly = keepObjId !== null ? this.entityPolyIds.get(keepObjId) : undefined;
    const keptInvis = keepObjId !== null ? this.entityInvisTypes.get(keepObjId) : undefined;
    const keptLight = keepObjId !== null ? this.entityLights.get(keepObjId) : undefined;

    this.entityWeapons.clear();
    this.entityLawfuls.clear();
    this.entityPolyIds.clear();
    this.entityInvisTypes.clear();
    this.entityLights.clear();

    if (keepObjId !== null) {
      if (keptWeapon !== undefined) this.entityWeapons.set(keepObjId, keptWeapon);
      if (keptLawful !== undefined) this.entityLawfuls.set(keepObjId, keptLawful);
      if (keptPoly !== undefined) this.entityPolyIds.set(keepObjId, keptPoly);
      if (keptInvis !== undefined) this.entityInvisTypes.set(keepObjId, keptInvis);
      if (keptLight !== undefined) this.entityLights.set(keepObjId, keptLight);
    }
  }

  /**
   * 범위 내 엔티티 조회
   */
  getEntitiesInRange(x: number, y: number, range: number): Entity[] {
    const result: Entity[] = [];
    const rangeSq = range * range;

    for (const player of this.players.values()) {
      const dx = player.x - x;
      const dy = player.y - y;
      if (dx * dx + dy * dy <= rangeSq) result.push(player);
    }
    for (const npc of this.npcs.values()) {
      const dx = npc.x - x;
      const dy = npc.y - y;
      if (dx * dx + dy * dy <= rangeSq) result.push(npc);
    }
    for (const monster of this.monsters.values()) {
      const dx = monster.x - x;
      const dy = monster.y - y;
      if (dx * dx + dy * dy <= rangeSq) result.push(monster);
    }

    return result;
  }

  // ==================== 캐릭터 목록 ====================

  /**
   * 캐릭터 목록 설정 (캐릭터 선택 화면용)
   */
  setCharacterList(characters: CharacterInfo[]): void {
    // 게이트웨이 payload 형태가 바뀌어도 여기서 터지지 않도록 방어한다.
    this.characterList = Array.isArray(characters) ? [...characters] : [];
    this.emitCharacterList();
  }

  /**
   * 캐릭터 목록에 누적한다 (같은 이름은 다시 넣지 않는다).
   *
   * 게이트웨이가 캐릭터를 한 명씩 따로 보내기 때문에 필요하다.
   * 목록 비우기는 reset()이 담당한다 (resetInGame()은 캐릭터 선택 화면으로
   * 돌아가는 경로라 목록을 그대로 둔다).
   */
  private addCharacters(characters: CharacterInfo[]): void {
    let changed = false;

    for (const character of characters) {
      if (!character?.name) continue;
      if (this.characterList.some((c) => c.name === character.name)) continue;
      this.characterList.push(character);
      changed = true;
    }

    if (changed) this.emitCharacterList();
  }

  private emitCharacterList(): void {
    this.events.emit('charList:update', { characters: this.characterList });
    gameEvents.emit('char:list', { characters: this.characterList });
  }

  /**
   * 캐릭터 목록 반환
   */
  getCharacterList(): CharacterInfo[] {
    return [...this.characterList];
  }

  // ==================== 순간이동 북마크 ====================

  /**
   * 북마크 목록을 통째로 교체한다 (S_BookmarkLoad 수신용).
   *
   * 서버는 목록을 부분 갱신하지 않고 언제나 전체를 다시 보내므로 누적하지 않는다
   * (캐릭터 목록과 다른 점 — char_list는 한 명씩 오지만 북마크는 한 번에 온다).
   * 유효하지 않은 항목(이름/id 없음)은 걸러 UI가 빈 줄을 그리지 않게 한다.
   */
  setBookmarks(bookmarks: BookmarkEntry[]): void {
    const list = Array.isArray(bookmarks) ? bookmarks : [];
    this.bookmarks = list
      .filter((b) => b && typeof b.id === 'number')
      .map((b) => ({
        id: b.id,
        name: b.name ?? '',
        mapId: b.mapId ?? 0,
        x: b.x ?? 0,
        y: b.y ?? 0,
      }));

    this.events.emit('bookmarks:update', { bookmarks: this.getBookmarks() });
    gameEvents.emit('bookmarks:update', { bookmarks: this.getBookmarks() });
  }

  /**
   * 순간이동 북마크 목록.
   *
   * 순간이동 주문서(use_type 6/29)를 쓸 때 여기서 고른 항목의 `mapId`와 `id`를
   * `sendUseItem(itemId, { useType, mapId: b.mapId, targetId: b.id })`로 보낸다.
   */
  getBookmarks(): BookmarkEntry[] {
    return this.bookmarks.map((b) => ({ ...b }));
  }

  /** 특정 북마크 조회 (없으면 undefined). */
  getBookmark(id: number): BookmarkEntry | undefined {
    const found = this.bookmarks.find((b) => b.id === id);
    return found ? { ...found } : undefined;
  }

  // ==================== 인벤토리 ====================

  /**
   * 인벤토리 설정
   */
  setInventory(items: Item[]): void {
    this.inventory = [...items];
    this.emitInventoryUpdate();
  }

  /**
   * 인벤토리 변경을 알리는 단일 경로.
   *
   * 인벤토리 UI(Inventory / QuickSlotBar)는 gameEvents의 'inventory:update'만
   * 구독하므로, 어떤 경로로 목록이 바뀌든 반드시 이 이벤트가 나가야 화면이 갱신된다.
   */
  private emitInventoryUpdate(): void {
    this.events.emit('inventory:update', { items: this.inventory });
    gameEvents.emit('inventory:update', { items: this.getInventory() });
  }

  /**
   * 인벤토리 반환
   */
  getInventory(): Item[] {
    return [...this.inventory];
  }

  /**
   * 인벤토리에 아이템 추가
   */
  addItem(item: Item): void {
    this.inventory.push(item);
    // 개별 통지('inventory:add')와 목록 갱신을 모두 낸다.
    // 목록 갱신을 빠뜨리면 새로 얻은 아이템이 인벤토리 화면에 나타나지 않는다.
    gameEvents.emit('inventory:add', { item });
    this.emitInventoryUpdate();
  }

  /**
   * 인벤토리에 아이템 추가 또는 병합 (add_item 수신용)
   * 같은 id(objId)의 아이템이 이미 있으면 count 등 필드를 갱신하고, 없으면 새로 추가한다.
   */
  upsertItem(item: Item): void {
    const existing = this.inventory.find(i => i.id === item.id);
    if (existing) {
      existing.count = item.count;
      existing.gfxId = item.gfxId;
      existing.name = item.name || existing.name;
      existing.identified = item.identified;
      if (item.useType !== undefined) existing.useType = item.useType;
      this.emitInventoryUpdate();
    } else {
      this.addItem(item);
    }
  }

  /**
   * 인벤토리 아이템의 개수/표시명을 갱신한다 (S_ItemAmount 수신용).
   *
   * **소비형 아이템(포션·화살 등)을 쓰면 서버는 이 패킷만 보낸다.**
   * 개수가 0이 되어야 비로소 S_DeleteInventoryItem이 오므로, 여기서 반영하지
   * 않으면 "포션을 마셔도 개수가 그대로"인 증상이 생긴다.
   * count가 0 이하이면 방어적으로 목록에서 제거한다.
   *
   * @param objId 아이템 objId (클라이언트 Item.id)
   * @param count 갱신된 개수
   * @param name  서버 getViewName() 표시명 (없으면 기존 이름 유지)
   */
  updateItemAmount(objId: number, count: number, name?: string): void {
    const item = this.inventory.find((i) => i.id === objId);
    if (!item) return;

    if (count <= 0) {
      this.removeItem(objId);
      return;
    }

    // 서버가 보내는 name_id($숫자) 패턴을 한글로 변환한다 (normalizeItem과 동일 규칙).
    const nextName = name ? resolveNpcName(name) : item.name;
    if (item.count === count && item.name === nextName) return;

    item.count = count;
    item.name = nextName;
    this.emitInventoryUpdate();
  }

  /**
   * 인벤토리 아이템의 표시명을 갱신한다 (S_ItemName 수신용).
   *
   * 이름이 곧 장착 상태를 담고 있으므로, 이 갱신이 장비창·인벤토리 표시를 바꾼다.
   */
  renameItem(objId: number, name: string): void {
    const item = this.inventory.find((i) => i.id === objId);
    if (!item || !name || item.name === name) return;

    item.name = name;
    this.emitInventoryUpdate();
  }

  /**
   * 인벤토리에서 아이템 제거
   */
  removeItem(itemId: number): void {
    const index = this.inventory.findIndex(i => i.id === itemId);
    if (index !== -1) {
      const removed = this.inventory.splice(index, 1)[0];
      // 개별 통지('inventory:remove')와 목록 갱신을 모두 낸다.
      // 목록 갱신을 빠뜨리면 다 쓴 아이템이 인벤토리 화면에 남는다.
      gameEvents.emit('inventory:remove', { item: removed });
      this.emitInventoryUpdate();
    }
  }

  // ==================== NPC 상점 ====================

  /**
   * NPC가 파는 물건 목록을 보관하고 알린다 (S_ShopSellList 254).
   *
   * ⚠ **이름이 서버 관점이라 플레이어에게는 "구매" 목록이다** — `npc_action("buy")`의
   * 응답이 이 패킷이다. 목록은 부분 갱신되지 않고 창을 열 때마다 통째로 오므로 교체한다.
   *
   * 표시명은 `$숫자` 리소스 키로 올 수 있어 인벤토리와 같은 규칙으로 해석한다.
   * **묶음 판매의 `"이름 (1000)"` 꼬리는 떼지 않는다** — 인벤토리의 소지 개수와 달리
   * 한 번 사면 받는 개수이고 가격도 그 묶음 값이라 화면에 남아야 한다.
   */
  setShopSellList(payload: ShopSellListPayload): void {
    const items: ShopSellListItem[] = (payload.items ?? []).map((item) => ({
      index: item.index ?? 0,
      gfxId: item.gfxId ?? 0,
      price: item.price ?? 0,
      name: resolveItemName(item.name ?? ''),
    }));

    this.shopSellList = { objId: payload.objId, currency: payload.currency ?? 0, items };

    const emitted = this.getShopSellList()!;
    this.events.emit('shop:sellList', emitted);
    gameEvents.emit('shop:sellList', emitted);
  }

  /**
   * NPC가 사주는 값 목록을 보관하고 알린다 (S_ShopBuyList 170).
   *
   * ⚠ **이름이 서버 관점이라 플레이어에게는 "판매" 목록이다** — `npc_action("sell")`의
   * 응답이다.
   *
   * **서버는 objId와 감정가만 보낸다** (`S_ShopBuyList.java`는 `writeD` 두 번이 전부다).
   * 원본 클라이언트가 자기 인벤토리에서 나머지를 찾아 붙이므로 여기서도 같은 조인을 한다.
   * 인벤토리에 없는 objId면 이름 없이 그대로 두어 UI가 최소한 가격은 그릴 수 있게 한다.
   *
   * 이름 비교/표시는 반드시 `stripCountSuffix()`를 거친다 — 서버는 겹치는 아이템의
   * 표시명 꼬리에 개수를 박아 보내며(`"물약 (7)"`), 개수의 진실은 `count` 필드뿐이다.
   */
  setShopBuyList(payload: ShopBuyListPayload): void {
    const byObjId = new Map(this.inventory.map((item) => [item.id, item]));

    const items: ShopBuyEntry[] = (payload.items ?? []).map((entry) => {
      const found = byObjId.get(entry.objId);
      return {
        objId: entry.objId,
        price: entry.price ?? 0,
        // 인벤토리의 name은 서버 원본이라 `$26` 같은 이름 ID가 그대로 들어 있다.
        // 꼬리 개수를 먼저 떼야 `"$26 (2)"`가 `$26`으로 매핑에 걸린다
        // (구매 목록 쪽 resolveItemName과 같은 처리 — 여기만 빠져 있어 화면에
        //  `$26`이 그대로 노출됐다).
        name: found ? resolveShopItemName(found.name) : undefined,
        gfxId: found?.gfxId,
        count: found?.count,
      };
    });

    this.shopBuyList = { objId: payload.objId, currency: payload.currency ?? 0, items };

    const emitted = this.getShopBuyList()!;
    this.events.emit('shop:buyList', emitted);
    gameEvents.emit('shop:buyList', emitted);
  }

  /** NPC가 파는 물건 목록 (= 플레이어의 구매 목록). 열린 상점이 없으면 null. */
  getShopSellList(): ShopSellListPayload | null {
    if (!this.shopSellList) return null;
    return { ...this.shopSellList, items: this.shopSellList.items.map((i) => ({ ...i })) };
  }

  /** NPC가 사주는 값 목록 (= 플레이어의 판매 목록). 열린 상점이 없으면 null. */
  getShopBuyList(): ShopBuyListState | null {
    if (!this.shopBuyList) return null;
    return { ...this.shopBuyList, items: this.shopBuyList.items.map((i) => ({ ...i })) };
  }

  /**
   * 상점 목록을 비운다 (창을 닫거나 맵이 바뀔 때).
   *
   * 남겨 두면 다른 마을의 상점 창을 열었을 때 이전 NPC의 목록이 먼저 그려지고,
   * 그 상태에서 산 항목의 index가 엉뚱한 물건을 가리킨다.
   * 비울 것이 있을 때만 `shop:close`를 알린다.
   */
  clearShopLists(): void {
    if (!this.shopSellList && !this.shopBuyList) return;

    this.shopSellList = null;
    this.shopBuyList = null;
    this.events.emit('shop:close', {});
    gameEvents.emit('shop:close', {});
  }

  // ==================== NPC 창고 ====================

  /**
   * 창고 목록을 보관하고 알린다 (`npc_action("retrieve")`의 응답).
   *
   * 목록은 부분 갱신되지 않고 창을 열 때마다 통째로 오므로 교체한다.
   *
   * 표시명은 상점 판매 목록과 똑같이 서버 원본(`"$326 ($11)"`)이라
   * `resolveShopItemName()`으로 해석한다 — 개수 꼬리를 떼고 이름 ID와 상태
   * 라벨까지 바꿔야 화면에 `$26`이 그대로 노출되지 않는다. 개수의 진실은
   * `count` 필드뿐이다.
   *
   * 상태 바이트는 감정 상태(0~3)와 봉인 비트(128)가 한 값에 섞여 오므로
   * 여기서 풀어 두고 원본도 함께 남긴다.
   */
  setWarehouseList(payload: RetrieveListPayload): void {
    const items: WarehouseEntry[] = (payload.items ?? []).map((item) => {
      const status = item.status ?? 0;
      return {
        objId: item.objId ?? 0,
        gfxId: item.gfxId ?? 0,
        count: item.count ?? 0,
        identified: item.identified ?? false,
        name: resolveShopItemName(item.name ?? ''),
        status,
        blessStatus: status & ~WAREHOUSE_SEALED_FLAG,
        sealed: (status & WAREHOUSE_SEALED_FLAG) !== 0,
      };
    });

    this.warehouseList = {
      objId: payload.objId,
      warehouseType: payload.warehouseType ?? 0,
      fee: payload.fee ?? 0,
      items,
    };

    const emitted = this.getWarehouseList()!;
    this.events.emit('warehouse:list', emitted);
    gameEvents.emit('warehouse:list', emitted);
  }

  /** 창고에 들어 있는 물건 목록. 열린 창고가 없으면 null. */
  getWarehouseList(): WarehouseListState | null {
    if (!this.warehouseList) return null;
    return { ...this.warehouseList, items: this.warehouseList.items.map((i) => ({ ...i })) };
  }

  /**
   * 창고 목록을 비운다 (창을 닫거나 맵이 바뀔 때).
   *
   * 남겨 두면 다른 창고를 열었을 때 이전 목록이 먼저 그려지고, 그 상태에서
   * 인출을 누르면 이미 없는 objId를 보내게 된다 (서버는 조용히 무시한다).
   * 비울 것이 있을 때만 `warehouse:close`를 알린다.
   */
  clearWarehouseList(): void {
    if (!this.warehouseList) return;

    this.warehouseList = null;
    this.events.emit('warehouse:close', {});
    gameEvents.emit('warehouse:close', {});
  }

  // ==================== 채팅 ====================

  /**
   * 채팅 메시지 추가
   */
  addChatMessage(msg: ChatMessage): void {
    this.chatMessages.push(msg);
    if (this.chatMessages.length > this.maxChatMessages) {
      this.chatMessages = this.chatMessages.slice(-this.maxChatMessages);
    }
    this.events.emit('chat:message', msg);
    gameEvents.emit('chat:message', msg);
  }

  /**
   * 채팅 메시지 목록 반환
   */
  getChatMessages(): ChatMessage[] {
    return [...this.chatMessages];
  }

  /**
   * 채팅 메시지 초기화
   */
  clearChatMessages(): void {
    this.chatMessages = [];
    this.events.emit('chat:clear', {});
    gameEvents.emit('chat:clear', {});
  }

  // ==================== 세계 상태 ====================

  /**
   * 전체 세계 상태 반환
   */
  getWorldState(): WorldState {
    return {
      mapId: this.mapId,
      players: new Map(this.players),
      npcs: new Map(this.npcs),
      monsters: new Map(this.monsters),
    };
  }

  // ==================== 이벤트 ====================

  /**
   * 내부 이벤트 구독 공통 처리 — 등록 해제 함수를 반환한다.
   */
  private subscribe<T>(event: string, callback: (data: T) => void): () => void {
    this.events.on(event, callback);
    return () => this.events.off(event, callback);
  }

  /**
   * 플레이어 스폰 이벤트 리스너
   * @returns 등록 해제 함수
   */
  onPlayerSpawn(callback: (data: { objId: number; x: number; y: number }) => void): () => void {
    return this.subscribe('player:set', callback);
  }

  /**
   * 플레이어 이동 이벤트 리스너
   * @returns 등록 해제 함수
   */
  onPlayerMove(callback: (data: { x: number; y: number; heading: number }) => void): () => void {
    return this.subscribe('player:position', callback);
  }

  /**
   * 플레이어 스탯 업데이트 이벤트 리스너
   * @returns 등록 해제 함수
   */
  onStatsUpdate(callback: (data: { hp: number; mp: number; maxHp: number; maxMp: number }) => void): () => void {
    return this.subscribe('player:stats', callback);
  }

  /**
   * 엔티티 스폰 이벤트 리스너
   * @returns 등록 해제 함수
   */
  onEntitySpawn(callback: (data: { type: string; entity: Entity }) => void): () => void {
    return this.subscribe('entity:spawn', callback);
  }

  /**
   * 엔티티 제거 이벤트 리스너
   * @returns 등록 해제 함수
   */
  onEntityRemove(callback: (data: { objId: number }) => void): () => void {
    return this.subscribe('entity:remove', callback);
  }

  /**
   * 엔티티 이동 이벤트 리스너
   * @returns 등록 해제 함수
   */
  onEntityMove(callback: (data: { objId: number; x: number; y: number; heading: number; type: string }) => void): () => void {
    return this.subscribe('entity:move', callback);
  }

  /**
   * 맵 변경 이벤트 리스너
   * @returns 등록 해제 함수
   */
  onMapChange(callback: (data: { mapId: number }) => void): () => void {
    return this.subscribe('map:change', callback);
  }

  /**
   * 인벤토리 업데이트 이벤트 리스너
   * @returns 등록 해제 함수
   */
  onInventoryUpdate(callback: (data: { items: Item[] }) => void): () => void {
    return this.subscribe('inventory:update', callback);
  }

  /**
   * 채팅 메시지 이벤트 리스너
   * @returns 등록 해제 함수
   */
  onChatMessage(callback: (msg: ChatMessage) => void): () => void {
    return this.subscribe('chat:message', callback);
  }

  /**
   * 엔티티 외형(장착 무기) 변경 이벤트 리스너 — S_CharVisualUpdate
   * @returns 등록 해제 함수
   */
  onEntityVisual(callback: (data: { objId: number; weapon: number; isPlayer: boolean }) => void): () => void {
    return this.subscribe('entity:visual', callback);
  }

  /**
   * 성향치 변경 이벤트 리스너 — S_Lawful
   * @returns 등록 해제 함수
   */
  onEntityLawful(callback: (data: { objId: number; lawful: number; isPlayer: boolean }) => void): () => void {
    return this.subscribe('entity:lawful', callback);
  }

  /**
   * 변신 변경 이벤트 리스너 — S_ChangeShape (polyId 0 = 변신 해제)
   * @returns 등록 해제 함수
   */
  onEntityPoly(callback: (data: { objId: number; polyId: number; weaponTakeoff: boolean; isPlayer: boolean }) => void): () => void {
    return this.subscribe('entity:poly', callback);
  }

  /**
   * 투명 상태 변경 이벤트 리스너 — S_Invis
   * @returns 등록 해제 함수
   */
  onEntityInvis(callback: (data: { objId: number; invisType: number; invisible: boolean; isPlayer: boolean }) => void): () => void {
    return this.subscribe('entity:invis', callback);
  }

  /**
   * 광원 반경 변경 이벤트 리스너 — S_Light
   * @returns 등록 해제 함수
   */
  onEntityLight(callback: (data: { objId: number; lightSize: number; isPlayer: boolean }) => void): () => void {
    return this.subscribe('entity:light', callback);
  }

  /**
   * 방향 전환 이벤트 리스너 — S_ChangeHeading (제자리 회전, 좌표 없음)
   * @returns 등록 해제 함수
   */
  onEntityHeading(callback: (data: { objId: number; heading: number; isPlayer: boolean }) => void): () => void {
    return this.subscribe('entity:heading', callback);
  }

  /**
   * 상태이상 변경 이벤트 리스너 — S_Paralysis
   * @returns 등록 해제 함수
   */
  onParalysis(callback: (data: { kind: number; paralyzed: boolean; teleportUnlock: boolean }) => void): () => void {
    return this.subscribe('player:paralysis', callback);
  }

  /**
   * 텔레포트 잠금 변경 이벤트 리스너 — S_Teleport / 잠금 해제
   * @returns 등록 해제 함수
   */
  onTeleporting(callback: (data: { teleporting: boolean }) => void): () => void {
    return this.subscribe('player:teleport', callback);
  }

  /**
   * 캐릭터 목록 업데이트 이벤트 리스너
   * @returns 등록 해제 함수
   */
  onCharListUpdate(callback: (data: { characters: CharacterInfo[] }) => void): () => void {
    return this.subscribe('charList:update', callback);
  }

  /**
   * 순간이동 북마크 목록 갱신 이벤트 리스너 — S_BookmarkLoad
   * @returns 등록 해제 함수
   */
  onBookmarksUpdate(callback: (data: { bookmarks: BookmarkEntry[] }) => void): () => void {
    return this.subscribe('bookmarks:update', callback);
  }

  /**
   * NPC가 파는 물건 목록 이벤트 리스너 — S_ShopSellList (**플레이어의 "구매" 목록**)
   * @returns 등록 해제 함수
   */
  onShopSellList(callback: (data: ShopSellListPayload) => void): () => void {
    return this.subscribe('shop:sellList', callback);
  }

  /**
   * NPC가 사주는 값 목록 이벤트 리스너 — S_ShopBuyList (**플레이어의 "판매" 목록**)
   * @returns 등록 해제 함수
   */
  onShopBuyList(callback: (data: ShopBuyListState) => void): () => void {
    return this.subscribe('shop:buyList', callback);
  }

  /**
   * 상점 목록이 비워졌음을 알리는 이벤트 리스너 — 창 닫기 / 맵 변경 / 리셋
   * @returns 등록 해제 함수
   */
  onShopClose(callback: () => void): () => void {
    return this.subscribe('shop:close', callback);
  }

  /**
   * 창고 목록 이벤트 리스너 — `npc_action("retrieve")`의 응답
   * @returns 등록 해제 함수
   */
  onWarehouseList(callback: (data: WarehouseListState) => void): () => void {
    return this.subscribe('warehouse:list', callback);
  }

  /**
   * 창고 목록이 비워졌음을 알리는 이벤트 리스너 — 창 닫기 / 맵 변경 / 리셋
   * @returns 등록 해제 함수
   */
  onWarehouseClose(callback: () => void): () => void {
    return this.subscribe('warehouse:close', callback);
  }

  // ==================== 리셋 ====================

  /**
   * 게임 상태 초기화
   */
  reset(): void {
    this.playerObjId = null;
    this.playerX = 0;
    this.playerY = 0;
    this.playerHeading = 0;
    this.detailStats = null;
    this.dead = false;
    this.hp = 0;
    this.mp = 0;
    this.maxHp = 0;
    this.maxMp = 0;
    this.level = 1;
    this.mapId = 0;
    this.mapIdKnown = false;
    this.players.clear();
    this.npcs.clear();
    this.monsters.clear();
    this.inventory = [];
    this.chatMessages = [];
    this.characterList = [];
    this.resetTransientState();

    this.events.emit('reset', {});
    gameEvents.emit('gameState:reset', {});
  }

  /**
   * 게임 중 상태만 초기화 (로그아웃 시)
   */
  resetInGame(): void {
    this.playerObjId = null;
    this.playerX = 0;
    this.playerY = 0;
    this.playerHeading = 0;
    this.detailStats = null;
    this.dead = false;
    this.hp = 0;
    this.mp = 0;
    this.maxHp = 0;
    this.maxMp = 0;
    this.level = 1;
    this.mapId = 0;
    this.mapIdKnown = false;
    this.players.clear();
    this.npcs.clear();
    this.monsters.clear();
    this.inventory = [];
    this.chatMessages = [];
    this.resetTransientState();

    this.events.emit('resetInGame', {});
    gameEvents.emit('gameState:resetInGame', {});
  }

  /**
   * 텔레포트/상태이상/엔티티 부가 상태를 비운다.
   *
   * 이 값들을 남겨 두면 다음 접속에서 이동이 잠긴 채로 시작할 수 있으므로
   * reset()과 resetInGame() 양쪽에서 반드시 호출한다.
   */
  private resetTransientState(): void {
    this.clearTeleportTimer();
    this.teleporting = false;
    this.paralysisKind = null;
    this.paralyzed = false;
    this.entityWeapons.clear();
    this.entityLawfuls.clear();
    this.entityPolyIds.clear();
    this.entityInvisTypes.clear();
    this.entityLights.clear();
    // 북마크는 캐릭터별 데이터다. 캐릭터 선택 화면으로 돌아가면 반드시 비워야
    // 다음 캐릭터에서 남의 북마크로 순간이동을 시도하지 않는다.
    this.bookmarks = [];
    // 열려 있던 상점/창고 목록도 캐릭터/세션과 함께 버린다.
    // 창고는 캐릭터별 데이터라 남기면 다음 캐릭터에서 남의 목록이 보인다.
    this.clearShopLists();
    this.clearWarehouseList();
  }
}

/**
 * 기본 인스턴스 내보내기
 */
export const gameState = GameState.getInstance();

/**
 * 기본 내보내기
 */
export default gameState;
